import { type Blueprint } from '@rta/rta-blueprint-library';

import { BlueprintCombinedPanelCard } from '../shared/blueprint-combined-panel-card';
import { BlueprintPageFooter } from '../shared/blueprint-page-footer';
import { BlueprintPageHeader } from '../shared/blueprint-page-header';
import { BlueprintPageWrapper } from '../shared/blueprint-page-wrapper';
import { BlueprintPanelFooter } from '../shared/blueprint-panel-footer';
import { getPageNoteScope } from '../helpers/get-page-note-scope';
import { hasLevelingLegs, LEVELING_LEGS_PANEL_NOTE } from '../helpers/has-leveling-legs';
import { getPageOverrideScope } from '../helpers/get-page-override-scope';
import { PAGE_BODY_PADDING } from '../shared/blueprint-view-styles';
import type { BlueprintPage } from '../types';

export type BlueprintCombinedFrontBackPageProps = {
	page: BlueprintPage;
	blueprint: Blueprint;
	customerName?: string;
	layoutDescription?: string;
	designerName?: string;
	creationDate?: string;
	threeDImageUrl?: string;
};

export const BlueprintCombinedFrontBackPage = (props: BlueprintCombinedFrontBackPageProps): React.ReactElement => {
	const {
		page,
		blueprint,
		customerName,
		layoutDescription,
		designerName,
		creationDate,
		threeDImageUrl
	} = props;

	const kitchen = blueprint.kitchens.getActiveVersion();
	const island = kitchen?.design.islands.findById(page.islandId);
	const frontPanelId = island?.panels.frontPanelId ?? null;
	const backPanels = island?.panels.getBackPanels() ?? [];
	const backPanelId = backPanels.length > 0 ? backPanels[0].id : null;

	const notes = [
		'Please Note: This view is the exterior side of the panel'
	];
	if (kitchen && hasLevelingLegs({ kitchen, islandId: page.islandId ?? null })) notes.push(LEVELING_LEGS_PANEL_NOTE);

	return (
		<BlueprintPageWrapper pageNumber={page.pageNumber}>
			{/* Child 1: Header */}
			<BlueprintPageHeader
				title={page.title}
				pageNumber={page.pageNumber}
				customerName={customerName}
				layoutDescription={layoutDescription}
				designerName={designerName}
				creationDate={creationDate}
				threeDImageUrl={threeDImageUrl}
				showThumbnail={true}
			/>

			{/* Child 2: Content (flex: 1) — both panels share one SVG so the gap
			    between them is the same 18 model-inches as the side-panels page. */}
			<div style={{
				flex: 1,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				overflow: 'hidden',
				padding: PAGE_BODY_PADDING
			}}>
				{frontPanelId && backPanelId && (
					<BlueprintCombinedPanelCard
						blueprint={blueprint}
						islandId={page.islandId}
						pageIndex={page.pageNumber}
						panels={[
							{ panelId: frontPanelId, topLabel: 'Front', showSidewaysFrontLabel: true },
							{ panelId: backPanelId, topLabel: 'Back', showSidewaysFrontLabel: true }
						]}
					/>
				)}
			</div>

			{/* Child 3: Combined panel footer + page footer */}
			<div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '14px' }}>
				<BlueprintPanelFooter scope={getPageOverrideScope(page)} notes={notes} noteScope={getPageNoteScope(page)} />
				<BlueprintPageFooter pageNumber={page.pageNumber} />
			</div>
		</BlueprintPageWrapper>
	);
};
